import React, { useState, useEffect, useCallback } from "react";
import { apiUrl } from "../../lib/config";

// ── Types ────────────────────────────────────────────────────────────────────

type Status = "operational" | "degraded" | "outage" | "maintenance";

interface ContextData {
  services: { id: string; name: string }[];
  regions: { id: string; name: string; service_id: string }[];
  service_regions: { id: string; service_id: string; region_id: string; component_name: string | null }[];
}

interface Pod {
  name: string;
  namespace: string;
  containers: string[];
  restarts: number;
  node: string;
  image_tag: string;
  age_seconds: number;
  phase: string;
  ready: string;
  controlled_by: string;
  cpu: string;
  memory: string;
}

interface Props {
  status: Status;
  onClose: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<Status, string> = {
  operational: "#107c10",
  degraded:    "#c19c00",
  outage:      "#d13438",
  maintenance: "#1a56aa",
};

function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function phaseClass(phase: string): string {
  switch (phase.toLowerCase()) {
    case "running":  return "aks-pod-phase--running";
    case "pending":  return "aks-pod-phase--pending";
    case "failed":   return "aks-pod-phase--failed";
    case "succeeded":return "aks-pod-phase--succeeded";
    default:         return "aks-pod-phase--unknown";
  }
}

// sessionStorage key — set after one silent SSO attempt to prevent redirect loops.
const AKS_AUTH_TRIED_KEY = "aks_auth_tried";

// ── Component ────────────────────────────────────────────────────────────────

export function AksExplorerDialog({ status, onClose }: Props) {
  const accent = STATUS_COLORS[status];

  // Access gate: server-side check via /api/v1/admin/aks-explorer/access/
  // Uses credentials so the browser sends the X_APTEAN_TOKEN cookie (which
  // may be HttpOnly and/or scoped to the parent domain .apteancloud.dev).
  const [hasAccess, setHasAccess] = useState<boolean | null>(null); // null = checking

  // Context (services/regions/service_regions for this status)
  const [ctx, setCtx] = useState<ContextData | null>(null);
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxError, setCtxError] = useState<string | null>(null);

  // Selections
  const [selectedService, setSelectedService] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedServiceRegion, setSelectedServiceRegion] = useState("");

  // Pods
  const [pods, setPods] = useState<Pod[]>([]);
  const [podsLoading, setPodsLoading] = useState(false);
  const [podsError, setPodsError] = useState<string | null>(null);

  // Selected pod + logs
  const [selectedPod, setSelectedPod] = useState<Pod | null>(null);
  const [activeContainer, setActiveContainer] = useState<string>("");
  const [logs, setLogs] = useState<Record<string, string>>({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Pagination
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  // Sorting
  type SortField = "name" | "namespace" | "restarts" | "phase" | "cpu" | "memory" | "age_seconds" | "node" | "controlled_by";
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  }

  function sortIcon(field: SortField) {
    if (sortField !== field) return <span className="aks-th-sort-icon">⇅</span>;
    return <span className="aks-th-sort-icon aks-th-sort-icon--active">{sortDir === "asc" ? "▲" : "▼"}</span>;
  }

  // ── Check access then load context ──────────────────────────────────────────────
  useEffect(() => {
    fetch(apiUrl("/api/v1/admin/aks-explorer/access/"), { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setHasAccess(Boolean(d.has_access)))
      .catch(() => setHasAccess(false));
  }, []);

  // If access is denied and we haven’t tried silent SSO yet, redirect through
  // Keycloak. Since the user is already logged in to AppCentral, Keycloak does
  // instant silent SSO — no login form — and redirects back after setting the
  // X_APTEAN_TOKEN cookie. The flag prevents an infinite loop when the user
  // genuinely lacks permission.
  useEffect(() => {
    if (hasAccess !== false) return;
    if (sessionStorage.getItem(AKS_AUTH_TRIED_KEY)) return;
    sessionStorage.setItem(AKS_AUTH_TRIED_KEY, "1");
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = apiUrl(`/api/v1/auth/iam/login/?next=${next}`);
  }, [hasAccess]);

  // ── Load context (only when access is granted) ────────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    setCtxLoading(true);
    fetch(apiUrl(`/api/v1/admin/aks-explorer/context/?status=${status}`), { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`Server returned ${r.status}`); return r.json(); })
      .then((d) => { setCtx(d); setCtxLoading(false); })
      .catch((e) => { setCtxError(e.message); setCtxLoading(false); });
  }, [hasAccess, status]);

  // Derived filtered lists
  const filteredRegions = ctx?.regions?.filter(r => r.service_id === selectedService) ?? [];
  const filteredServiceRegions = ctx?.service_regions?.filter(
    sr => sr.service_id === selectedService && sr.region_id === selectedRegion
  ) ?? [];

  // Auto-select when only one option
  useEffect(() => {
    if (ctx?.services.length === 1) setSelectedService(ctx.services[0].id);
  }, [ctx]);
  useEffect(() => {
    if (filteredRegions.length === 1) setSelectedRegion(filteredRegions[0].id);
    else setSelectedRegion("");
  }, [selectedService]);
  useEffect(() => {
    if (filteredServiceRegions.length === 1) setSelectedServiceRegion(filteredServiceRegions[0].id);
    else setSelectedServiceRegion("");
  }, [selectedRegion]);

  // ── Load pods when a service region is selected ───────────────────────────
  useEffect(() => {
    if (!selectedServiceRegion) { setPods([]); return; }
    setPodsLoading(true);
    setPodsError(null);
    setSelectedPod(null);
    setLogs({});
    fetch(apiUrl(`/api/v1/admin/aks-explorer/pods/?service_region_id=${selectedServiceRegion}`), { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setPodsError(d.error); setPods([]); }
        else setPods(d.pods ?? []);
        setPage(1);
        setPodsLoading(false);
      })
      .catch((e) => { setPodsError(e.message); setPodsLoading(false); });
  }, [selectedServiceRegion]);

  // ── Fetch logs for all containers of selected pod ─────────────────────────
  const fetchLogs = useCallback((pod: Pod) => {
    setSelectedPod(pod);
    setLogs({});
    setLogsError(null);
    setLogsLoading(true);
    // Exclude sidecar containers that are not application logs
    const EXCLUDED_CONTAINERS = ["fluentbit", "fluent-bit", "fluentd", "istio-proxy"];
    const appContainers = pod.containers.filter(
      (c) => !EXCLUDED_CONTAINERS.includes(c.toLowerCase())
    );
    const containersToFetch = appContainers.length > 0 ? appContainers : pod.containers;
    const first = containersToFetch[0] ?? "";
    setActiveContainer(first);

    const fetches = containersToFetch.map((container) =>
      fetch(
        apiUrl(`/api/v1/admin/aks-explorer/logs/?service_region_id=${selectedServiceRegion}&pod=${encodeURIComponent(pod.name)}&namespace=${encodeURIComponent(pod.namespace)}&container=${encodeURIComponent(container)}&tail=300`),
        { credentials: "include" }
      )
        .then((r) => r.json())
        .then((d) => ({ container, text: d.error ? `Error: ${d.error}` : (d.logs ?? "") }))
        .catch((e) => ({ container, text: `Error: ${e.message}` }))
    );

    Promise.all(fetches).then((results) => {
      const map: Record<string, string> = {};
      results.forEach(({ container, text }) => { map[container] = text; });
      setLogs(map);
      setLogsLoading(false);
    });
  }, [selectedServiceRegion]);

  const downloadVisibleLogs = useCallback(() => {
    if (!selectedPod) return;
    const content = logs[activeContainer] || "";
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedPod.name}-${activeContainer}.log`;
    a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  }, [selectedPod, activeContainer, logs]);

  const downloadAllLogs = useCallback(() => {
    if (!selectedPod || !selectedServiceRegion) return;
    const params = new URLSearchParams({
      service_region_id: selectedServiceRegion,
      pod: selectedPod.name,
      namespace: selectedPod.namespace,
      container: activeContainer,
    });
    const url = `/api/v1/admin/aks-explorer/logs/download/?${params.toString()}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedPod.name}-${activeContainer}.log`;
    a.click();
    setShowDownloadMenu(false);
  }, [selectedPod, activeContainer, selectedServiceRegion]);

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Close download menu on outside click
  useEffect(() => {
    if (!showDownloadMenu) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".aks-dialog__download-wrapper")) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDownloadMenu]);

  // Derive display label for the pods section heading
  const selectedRegionName = ctx?.regions?.find(r => r.id === selectedRegion)?.name ?? "";
  const selectedSrEntry = ctx?.service_regions?.find(sr => sr.id === selectedServiceRegion);
  const selectionLabel = selectedSrEntry?.component_name
    ? `${selectedRegionName} / ${selectedSrEntry.component_name}`
    : selectedRegionName;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="aks-dialog-overlay" role="dialog" aria-modal="true" aria-label={`AKS Explorer — ${status}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="aks-dialog">

        {/* Header */}
        <div className="aks-dialog__header" style={{ borderTopColor: accent }}>
          <div className="aks-dialog__header-left">
            <span className="aks-dialog__badge" style={{ background: accent }}>
              {status.toUpperCase()}
            </span>
            <h2 className="aks-dialog__title">AKS Explorer</h2>
          </div>
          <button type="button" className="aks-dialog__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="aks-dialog__body">

          {/* Verifying access or in the middle of silent SSO redirect */}
          {(hasAccess === null || (hasAccess === false && !sessionStorage.getItem(AKS_AUTH_TRIED_KEY))) && (
            <div className="aks-dialog__loading">Verifying access…</div>
          )}

          {/* Access denied — only after the silent SSO attempt returned false */}
          {hasAccess === false && sessionStorage.getItem(AKS_AUTH_TRIED_KEY) && (
            <div className="aks-dialog__access-denied">
              <span className="aks-dialog__access-denied-icon" aria-hidden="true">🔒</span>
              <p className="aks-dialog__access-denied-title">Access Denied</p>
              <p className="aks-dialog__access-denied-msg">
                You don&apos;t have access to view the logs.<br />
                Please contact the <strong>Aptean Platform Engineering Team</strong>.
              </p>
            </div>
          )}

          {ctxLoading && <div className="aks-dialog__loading">Loading context…</div>}
          {ctxError && <div className="aks-dialog__error">Failed to load context: {ctxError}</div>}

          {hasAccess && ctx && !ctxLoading && (
            <>
              {/* Cascading dropdowns */}
              <div className="aks-dialog__filters">
                <div className="aks-dialog__field">
                  <label className="aks-dialog__label" htmlFor="aks-service">Service</label>
                  <select
                    id="aks-service"
                    className="aks-dialog__select"
                    value={selectedService}
                    onChange={(e) => { setSelectedService(e.target.value); setSelectedRegion(""); setSelectedServiceRegion(""); }}
                  >
                    <option value="">-- Select service --</option>
                    {ctx.services.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="aks-dialog__field">
                  <label className="aks-dialog__label" htmlFor="aks-region">Region</label>
                  <select
                    id="aks-region"
                    className="aks-dialog__select"
                    value={selectedRegion}
                    disabled={!selectedService}
                    onChange={(e) => { setSelectedRegion(e.target.value); setSelectedServiceRegion(""); }}
                  >
                    <option value="">-- Select region --</option>
                    {filteredRegions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                {filteredServiceRegions.length > 1 && (
                  <div className="aks-dialog__field">
                    <label className="aks-dialog__label" htmlFor="aks-component">Component</label>
                    <select
                      id="aks-component"
                      className="aks-dialog__select"
                      value={selectedServiceRegion}
                      disabled={!selectedRegion}
                      onChange={(e) => setSelectedServiceRegion(e.target.value)}
                    >
                      <option value="">-- Select component --</option>
                      {filteredServiceRegions.map((sr) => (
                        <option key={sr.id} value={sr.id}>{sr.component_name ?? "(Default)"}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {ctx.services.length === 0 && (
                <div className="aks-dialog__empty">
                  No AKS endpoints are configured with <strong>{status}</strong> status.
                </div>
              )}

              {/* Pods table */}
              {selectedServiceRegion && (
                <div className="aks-dialog__pods-section">
                  <h3 className="aks-dialog__section-title">
                    Pods -- <span style={{ fontWeight: 400, color: "#605e5c" }}>{selectionLabel}</span>
                    {podsLoading && <span className="aks-dialog__spinner" />}
                  </h3>
                  {podsError && <div className="aks-dialog__error">{podsError}</div>}
                  {!podsLoading && !podsError && pods.length === 0 && (
                    <div className="aks-dialog__empty">No pods found in this namespace.</div>
                  )}
                  {pods.length > 0 && (() => {
                    // Sort
                    const sorted = [...pods].sort((a, b) => {
                      let av: string | number = a[sortField] ?? "";
                      let bv: string | number = b[sortField] ?? "";
                      if (sortField === "cpu" || sortField === "memory") {
                        // Sort numerically by parsing the trailing unit
                        const parse = (s: string) => parseFloat(s) || 0;
                        av = parse(av as string); bv = parse(bv as string);
                      }
                      if (av < bv) return sortDir === "asc" ? -1 : 1;
                      if (av > bv) return sortDir === "asc" ? 1 : -1;
                      return 0;
                    });
                    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
                    const pagedPods = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                    return (
                      <>
                        <div className="aks-pods-table-wrap">
                          <table className="aks-pods-table">
                            <thead>
                              <tr>
                                <th className="aks-th-sortable" onClick={() => handleSort("name")}>Name {sortIcon("name")}</th>
                                <th>Ready</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("restarts")}>Restarts {sortIcon("restarts")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("phase")}>Status {sortIcon("phase")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("cpu")}>CPU {sortIcon("cpu")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("memory")}>Memory {sortIcon("memory")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("age_seconds")}>Age {sortIcon("age_seconds")}</th>
                                <th>Containers</th>
                                <th>Image Tag</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("controlled_by")}>Controlled By {sortIcon("controlled_by")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("namespace")}>Namespace {sortIcon("namespace")}</th>
                                <th className="aks-th-sortable" onClick={() => handleSort("node")}>Node {sortIcon("node")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedPods.map((pod) => (
                                <tr
                                  key={`${pod.namespace}/${pod.name}`}
                                  className={`aks-pods-table__row${selectedPod?.name === pod.name && selectedPod?.namespace === pod.namespace ? " aks-pods-table__row--selected" : ""}`}
                                  onClick={() => fetchLogs(pod)}
                                  title="Click to view logs"
                                >
                                  <td className="aks-pods-table__name">{pod.name}</td>
                                  <td>{pod.ready}</td>
                                  <td>{pod.restarts}</td>
                                  <td>
                                    <span className={`aks-pod-phase ${phaseClass(pod.phase)}`}>{pod.phase}</span>
                                  </td>
                                  <td className="aks-pods-table__metric">{pod.cpu || <span className="aks-pods-table__metric--na">—</span>}</td>
                                  <td className="aks-pods-table__metric">{pod.memory || <span className="aks-pods-table__metric--na">—</span>}</td>
                                  <td>{fmtAge(pod.age_seconds)}</td>
                                  <td>{pod.containers.join(", ")}</td>
                                  <td className="aks-pods-table__image-tag" title={pod.image_tag}>{pod.image_tag || <span className="aks-pods-table__metric--na">—</span>}</td>
                                  <td>{pod.controlled_by}</td>
                                  <td className="aks-pods-table__namespace">{pod.namespace}</td>
                                  <td className="aks-pods-table__node">{pod.node}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Pagination */}
                        <div className="aks-pagination">
                          <span className="aks-pagination__info">
                            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length} pods
                          </span>
                          <div className="aks-pagination__controls">
                            <button
                              type="button"
                              className="aks-pagination__btn"
                              onClick={() => setPage(1)}
                              disabled={page === 1}
                              title="First page"
                            >«</button>
                            <button
                              type="button"
                              className="aks-pagination__btn"
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={page === 1}
                            >‹ Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                              .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push("…");
                                acc.push(p);
                                return acc;
                              }, [])
                              .map((item, idx) =>
                                item === "…" ? (
                                  <span key={`ellipsis-${idx}`} className="aks-pagination__ellipsis">…</span>
                                ) : (
                                  <button
                                    key={item}
                                    type="button"
                                    className={`aks-pagination__btn${page === item ? " aks-pagination__btn--active" : ""}`}
                                    onClick={() => setPage(item as number)}
                                  >{item}</button>
                                )
                              )}
                            <button
                              type="button"
                              className="aks-pagination__btn"
                              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                              disabled={page === totalPages}
                            >Next ›</button>
                            <button
                              type="button"
                              className="aks-pagination__btn"
                              onClick={() => setPage(totalPages)}
                              disabled={page === totalPages}
                              title="Last page"
                            >»</button>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Log viewer */}
              {selectedPod && (
                <div className="aks-dialog__logs-section">
                  <div className="aks-dialog__logs-header">
                    <h3 className="aks-dialog__section-title">
                      Logs — <span style={{ fontWeight: 400, color: "#605e5c" }}>{selectedPod.name}</span>
                      {logsLoading && <span className="aks-dialog__spinner" />}
                    </h3>
                    <button
                      type="button"
                      className="aks-dialog__refresh-btn"
                      onClick={() => fetchLogs(selectedPod)}
                      disabled={logsLoading}
                      title="Refresh logs"
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ verticalAlign: "middle", marginRight: "0.25rem" }}>
                        <path d="M13.5 2.5A7 7 0 1 0 15 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                        <polyline points="11,0 13.5,2.5 11,5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Refresh
                    </button>
                    <div className="aks-dialog__download-wrapper">
                      <button
                        type="button"
                        className="aks-dialog__download-btn"
                        onClick={() => setShowDownloadMenu((v) => !v)}
                        disabled={logsLoading || !logs[activeContainer]}
                        title="Download logs"
                      >
                        &#8595; Download
                      </button>
                      {showDownloadMenu && (
                        <div className="aks-dialog__download-menu">
                          <button type="button" className="aks-dialog__download-menu-item" onClick={downloadVisibleLogs}>
                            Visible logs
                          </button>
                          <button type="button" className="aks-dialog__download-menu-item" onClick={downloadAllLogs}>
                            All logs
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {logsError && <div className="aks-dialog__error">{logsError}</div>}

                  {/* Container tabs — sidecar containers (fluentbit etc.) are excluded */}
                  {Object.keys(logs).length > 1 && (
                    <div className="aks-log-tabs" role="tablist">
                      {Object.keys(logs).map((c) => (
                        <button
                          key={c}
                          type="button"
                          role="tab"
                          className={`aks-log-tab${activeContainer === c ? " aks-log-tab--active" : ""}`}
                          aria-selected={activeContainer === c}
                          onClick={() => setActiveContainer(c)}
                          style={activeContainer === c ? { borderBottomColor: accent, color: accent } : {}}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="aks-log-viewer" role="tabpanel">
                    {logsLoading ? (
                      <span style={{ color: "#888" }}>Fetching logs…</span>
                    ) : (
                      <pre className="aks-log-pre">
                        {logs[activeContainer] || "(no log output)"}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
